import type { LocaleModule } from '../types';

export interface AuthMessages {
  language: string;
  languageAria: string;
  usernameMethod: string;
  emailMethod: string;
  adminMethod: string;
  loginFailed: string;
  sendCodeFailed: string;
  codeSent: string;
  usernameRequired: string;
  username: string;
  adminUsername: string;
  emailRequired: string;
  emailInvalid: string;
  registeredEmail: string;
  passwordRequired: string;
  password: string;
  forgotPassword: string;
  codeRequired: string;
  codeInvalid: string;
  codePlaceholder: string;
  resendAfter: string;
  sendCode: string;
  termsRequired: string;
  termsPrefix: string;
  terms: string;
  termsAnd: string;
  privacy: string;
  loggingIn: string;
  enterAdmin: string;
  login: string;
  desktopLogin: string;
  mobileLogin: string;
  noAccount: string;
  registerNow: string;
  registerSuccess: string;
  registerSuccessDescription: string;
  registerFailed: string;
  usernameLength: string;
  usernamePattern: string;
  passwordLength: string;
  passwordHint: string;
  passwordStrength: string;
  strengthWeak: string;
  strengthFair: string;
  strengthGood: string;
  strengthStrong: string;
  registering: string;
  register: string;
  haveAccount: string;
  loginNow: string;
  recoverPassword: string;
  recoverSubtitle: string;
  verifyAccount: string;
  codeAndNewPassword: string;
  complete: string;
  operationFailed: string;
  resetSuccess: string;
  newPasswordRequired: string;
  passwordMinLength: string;
  newPasswordPlaceholder: string;
  confirmPasswordRequired: string;
  passwordsMismatch: string;
  confirmPasswordPlaceholder: string;
  resendShortAfter: string;
  previous: string;
  resetPassword: string;
  resendCodeAfter: string;
  resendCode: string;
  backToLogin: string;
  countryCodeRequired: string;
  phoneRequired: string;
  phoneInvalid: string;
  phonePlaceholder: string;
  loading: string;
  loadFailed: string;
  checkingLogin: string;
  initializingServices: string;
  pleaseWait: string;
  retryCount: string;
  initializationFailed: string;
  passwordTooShort: string;
  passwordTooLong: string;
  passwordNeedsLowercase: string;
  passwordNeedsUppercase: string;
  passwordNeedsNumber: string;
  passwordNeedsSpecial: string;
  otherDeviceLogin: string;
  sessionExpired: string;
  accountUnavailable: string;
  authUnavailable: string;
  loginAgain: string;
  loginError: string;
  operationError: string;
  networkError: string;
  invalidCredentials: string;
  serviceUnavailable: string;
  serverError: string;
  requestTimeout: string;
  invalidOrExpiredCode: string;
  emailCodeSent: string;
  resetError: string;
  passwordReset: string;
  badRequest: string;
  authenticationFailed: string;
  forbidden: string;
  usernameExists: string;
  emailExists: string;
  registrationError: string;
  safeLogout: string;
  profileLoadError: string;
  profileUpdateError: string;
  updateError: string;
  emailCodeToNewAddress: string;
  bindEmailError: string;
}

export const authLocales: LocaleModule<AuthMessages> = {
  zh_CN: {
    language: '语言',
    languageAria: '选择界面语言',
    usernameMethod: '用户名',
    emailMethod: '邮箱',
    adminMethod: '管理员',
    loginFailed: '登录失败',
    sendCodeFailed: '发送验证码失败',
    codeSent: '验证码已发送',
    usernameRequired: '请输入用户名',
    username: '用户名',
    adminUsername: '管理员用户名',
    emailRequired: '请输入邮箱',
    emailInvalid: '请输入有效的邮箱地址',
    registeredEmail: '注册邮箱',
    passwordRequired: '请输入密码',
    password: '密码',
    forgotPassword: '忘记密码？',
    codeRequired: '请输入验证码',
    codeInvalid: '验证码为 6 位数字',
    codePlaceholder: '6 位验证码',
    resendAfter: '{seconds} 秒后可重新发送',
    sendCode: '发送验证码',
    termsRequired: '请先阅读并同意用户协议和隐私政策',
    termsPrefix: '我已阅读并同意',
    terms: '用户协议',
    termsAnd: '和',
    privacy: '隐私政策',
    loggingIn: '登录中...',
    enterAdmin: '进入管理后台',
    login: '登录',
    desktopLogin: '电脑登录',
    mobileLogin: '移动登录',
    noAccount: '还没有账户？',
    registerNow: '立即注册',
    registerSuccess: '注册成功',
    registerSuccessDescription: '注册成功！欢迎加入国赫 WaveLink',
    registerFailed: '注册失败',
    usernameLength: '用户名长度必须在 3–32 个字符之间',
    usernamePattern: '用户名只能包含字母、数字、下划线和中文',
    passwordLength: '密码长度必须在 8–128 个字符之间',
    passwordHint: '密码（至少 8 位，包含大小写字母、数字和特殊字符）',
    passwordStrength: '密码强度：{level}',
    strengthWeak: '弱',
    strengthFair: '一般',
    strengthGood: '良好',
    strengthStrong: '强',
    registering: '注册中...',
    register: '注册',
    haveAccount: '已有账户？',
    loginNow: '立即登录',
    recoverPassword: '找回密码',
    recoverSubtitle: '通过邮箱验证码重置密码',
    verifyAccount: '验证账号',
    codeAndNewPassword: '验证码与新密码',
    complete: '完成',
    operationFailed: '操作失败',
    resetSuccess: '重置成功',
    newPasswordRequired: '请输入新密码',
    passwordMinLength: '密码至少 8 位',
    newPasswordPlaceholder: '新密码（至少 8 位）',
    confirmPasswordRequired: '请再次输入新密码',
    passwordsMismatch: '两次输入的新密码不一致',
    confirmPasswordPlaceholder: '确认新密码',
    resendShortAfter: '{seconds} 秒后可重发',
    previous: '上一步',
    resetPassword: '重置密码',
    resendCodeAfter: '{seconds} 秒后可重发验证码',
    resendCode: '重新发送验证码',
    backToLogin: '返回登录',
    countryCodeRequired: '请选择国家码',
    phoneRequired: '请输入手机号',
    phoneInvalid: '请输入有效的手机号（仅数字）',
    phonePlaceholder: '手机号',
    loading: '加载中...',
    loadFailed: '加载失败，请稍后重试。',
    checkingLogin: '正在检查登录状态...',
    initializingServices: '正在初始化服务...',
    pleaseWait: '请稍候，这可能需要几秒钟',
    retryCount: '重试次数：{count}/{max}',
    initializationFailed: '服务初始化失败，但应用将继续运行',
    passwordTooShort: '密码至少需要 8 个字符',
    passwordTooLong: '密码长度不能超过 128 个字符',
    passwordNeedsLowercase: '密码必须包含至少一个小写字母',
    passwordNeedsUppercase: '密码必须包含至少一个大写字母',
    passwordNeedsNumber: '密码必须包含至少一个数字',
    passwordNeedsSpecial: '密码必须包含至少一个特殊字符（如 !@#$%^&*）',
    otherDeviceLogin: '账号已在其他设备登录',
    sessionExpired: '会话已过期，请重新登录',
    accountUnavailable: '账户不可用，请联系管理员',
    authUnavailable: '认证服务暂时不可用，请稍后重试',
    loginAgain: '请重新登录',
    loginError: '登录失败，请稍后重试',
    operationError: '操作失败，请稍后重试',
    networkError: '网络连接失败，请检查网络设置后重试',
    invalidCredentials: '账号或密码错误，请检查后重试',
    serviceUnavailable: '服务暂时不可用，请稍后重试',
    serverError: '服务器错误，请稍后重试',
    requestTimeout: '请求超时，请检查网络连接后重试',
    invalidOrExpiredCode: '验证码错误或已失效，请重新获取',
    emailCodeSent: '若账号存在，验证码已发送至您的邮箱',
    resetError: '重置密码失败，请稍后重试',
    passwordReset: '密码已重置，请使用新密码登录',
    badRequest: '请求参数错误，请检查输入信息',
    authenticationFailed: '认证失败，请重新登录',
    forbidden: '权限不足，无法执行此操作',
    usernameExists: '用户名已存在，请选择其他用户名',
    emailExists: '邮箱已被注册，请使用其他邮箱',
    registrationError: '注册失败，请稍后重试',
    safeLogout: '已安全退出',
    profileLoadError: '获取用户信息失败',
    profileUpdateError: '更新个人信息失败',
    updateError: '更新失败',
    emailCodeToNewAddress: '验证码已发送至新邮箱，请查收',
    bindEmailError: '绑定邮箱失败',
  },
  zh_HK: {
    language: '語言',
    languageAria: '選擇介面語言',
    usernameMethod: '用戶名稱',
    emailMethod: '電郵',
    adminMethod: '管理員',
    loginFailed: '登入失敗',
    sendCodeFailed: '發送驗證碼失敗',
    codeSent: '驗證碼已發送',
    usernameRequired: '請輸入用戶名稱',
    username: '用戶名稱',
    adminUsername: '管理員用戶名稱',
    emailRequired: '請輸入電郵',
    emailInvalid: '請輸入有效的電郵地址',
    registeredEmail: '註冊電郵',
    passwordRequired: '請輸入密碼',
    password: '密碼',
    forgotPassword: '忘記密碼？',
    codeRequired: '請輸入驗證碼',
    codeInvalid: '驗證碼為 6 位數字',
    codePlaceholder: '6 位驗證碼',
    resendAfter: '{seconds} 秒後可重新發送',
    sendCode: '發送驗證碼',
    termsRequired: '請先閱讀並同意用戶協議和私隱政策',
    termsPrefix: '我已閱讀並同意',
    terms: '用戶協議',
    termsAnd: '和',
    privacy: '私隱政策',
    loggingIn: '登入中...',
    enterAdmin: '進入管理後台',
    login: '登入',
    desktopLogin: '電腦登入',
    mobileLogin: '行動裝置登入',
    noAccount: '還沒有帳戶？',
    registerNow: '立即註冊',
    registerSuccess: '註冊成功',
    registerSuccessDescription: '註冊成功！歡迎加入國赫 WaveLink',
    registerFailed: '註冊失敗',
    usernameLength: '用戶名稱長度必須為 3–32 個字元',
    usernamePattern: '用戶名稱只可包含字母、數字、底線和中文',
    passwordLength: '密碼長度必須為 8–128 個字元',
    passwordHint: '密碼（至少 8 位，包含大小寫字母、數字和特殊字元）',
    passwordStrength: '密碼強度：{level}',
    strengthWeak: '弱',
    strengthFair: '一般',
    strengthGood: '良好',
    strengthStrong: '強',
    registering: '註冊中...',
    register: '註冊',
    haveAccount: '已有帳戶？',
    loginNow: '立即登入',
    recoverPassword: '找回密碼',
    recoverSubtitle: '透過電郵驗證碼重設密碼',
    verifyAccount: '驗證帳戶',
    codeAndNewPassword: '驗證碼與新密碼',
    complete: '完成',
    operationFailed: '操作失敗',
    resetSuccess: '重設成功',
    newPasswordRequired: '請輸入新密碼',
    passwordMinLength: '密碼至少 8 位',
    newPasswordPlaceholder: '新密碼（至少 8 位）',
    confirmPasswordRequired: '請再次輸入新密碼',
    passwordsMismatch: '兩次輸入的新密碼不一致',
    confirmPasswordPlaceholder: '確認新密碼',
    resendShortAfter: '{seconds} 秒後可重發',
    previous: '上一步',
    resetPassword: '重設密碼',
    resendCodeAfter: '{seconds} 秒後可重發驗證碼',
    resendCode: '重新發送驗證碼',
    backToLogin: '返回登入',
    countryCodeRequired: '請選擇國家碼',
    phoneRequired: '請輸入手機號碼',
    phoneInvalid: '請輸入有效的手機號碼（只限數字）',
    phonePlaceholder: '手機號碼',
    loading: '載入中...',
    loadFailed: '載入失敗，請稍後再試。',
    checkingLogin: '正在檢查登入狀態...',
    initializingServices: '正在初始化服務...',
    pleaseWait: '請稍候，這可能需要幾秒鐘',
    retryCount: '重試次數：{count}/{max}',
    initializationFailed: '服務初始化失敗，但應用程式將繼續運行',
    passwordTooShort: '密碼至少需要 8 個字元',
    passwordTooLong: '密碼長度不可超過 128 個字元',
    passwordNeedsLowercase: '密碼必須包含至少一個小寫字母',
    passwordNeedsUppercase: '密碼必須包含至少一個大寫字母',
    passwordNeedsNumber: '密碼必須包含至少一個數字',
    passwordNeedsSpecial: '密碼必須包含至少一個特殊字元（如 !@#$%^&*）',
    otherDeviceLogin: '帳戶已在其他裝置登入',
    sessionExpired: '工作階段已過期，請重新登入',
    accountUnavailable: '帳戶不可用，請聯絡管理員',
    authUnavailable: '認證服務暫時不可用，請稍後再試',
    loginAgain: '請重新登入',
    loginError: '登入失敗，請稍後再試',
    operationError: '操作失敗，請稍後再試',
    networkError: '網絡連線失敗，請檢查網絡設定後再試',
    invalidCredentials: '帳戶或密碼錯誤，請檢查後再試',
    serviceUnavailable: '服務暫時不可用，請稍後再試',
    serverError: '伺服器錯誤，請稍後再試',
    requestTimeout: '請求逾時，請檢查網絡連線後再試',
    invalidOrExpiredCode: '驗證碼錯誤或已失效，請重新取得',
    emailCodeSent: '如帳戶存在，驗證碼已發送至您的電郵',
    resetError: '重設密碼失敗，請稍後再試',
    passwordReset: '密碼已重設，請使用新密碼登入',
    badRequest: '請求參數錯誤，請檢查輸入資料',
    authenticationFailed: '認證失敗，請重新登入',
    forbidden: '權限不足，無法執行此操作',
    usernameExists: '用戶名稱已存在，請選擇其他名稱',
    emailExists: '電郵已被註冊，請使用其他電郵',
    registrationError: '註冊失敗，請稍後再試',
    safeLogout: '已安全登出',
    profileLoadError: '取得用戶資料失敗',
    profileUpdateError: '更新個人資料失敗',
    updateError: '更新失敗',
    emailCodeToNewAddress: '驗證碼已發送至新電郵，請查收',
    bindEmailError: '綁定電郵失敗',
  },
  en_US: {
    language: 'Language',
    languageAria: 'Select interface language',
    usernameMethod: 'Username',
    emailMethod: 'Email',
    adminMethod: 'Admin',
    loginFailed: 'Sign-in failed',
    sendCodeFailed: 'Could not send verification code',
    codeSent: 'Verification code sent',
    usernameRequired: 'Enter your username',
    username: 'Username',
    adminUsername: 'Admin username',
    emailRequired: 'Enter your email address',
    emailInvalid: 'Enter a valid email address',
    registeredEmail: 'Registered email',
    passwordRequired: 'Enter your password',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    codeRequired: 'Enter the verification code',
    codeInvalid: 'The verification code must be 6 digits',
    codePlaceholder: '6-digit verification code',
    resendAfter: 'Resend in {seconds}s',
    sendCode: 'Send verification code',
    termsRequired: 'Please read and agree to the Terms of Service and Privacy Policy',
    termsPrefix: 'I have read and agree to the',
    terms: 'Terms of Service',
    termsAnd: 'and',
    privacy: 'Privacy Policy',
    loggingIn: 'Signing in...',
    enterAdmin: 'Open admin console',
    login: 'Sign in',
    desktopLogin: 'Desktop sign in',
    mobileLogin: 'Mobile sign in',
    noAccount: "Don't have an account?",
    registerNow: 'Register now',
    registerSuccess: 'Registration successful',
    registerSuccessDescription: 'Registration successful! Welcome to Guohe WaveLink',
    registerFailed: 'Registration failed',
    usernameLength: 'Username must be 3–32 characters',
    usernamePattern: 'Username may contain letters, numbers, underscores, and Chinese characters only',
    passwordLength: 'Password must be 8–128 characters',
    passwordHint: 'Password (8+ characters with upper/lowercase letters, a number, and a symbol)',
    passwordStrength: 'Password strength: {level}',
    strengthWeak: 'Weak',
    strengthFair: 'Fair',
    strengthGood: 'Good',
    strengthStrong: 'Strong',
    registering: 'Registering...',
    register: 'Register',
    haveAccount: 'Already have an account?',
    loginNow: 'Sign in now',
    recoverPassword: 'Reset password',
    recoverSubtitle: 'Reset your password using an email verification code',
    verifyAccount: 'Verify account',
    codeAndNewPassword: 'Code and new password',
    complete: 'Done',
    operationFailed: 'Operation failed',
    resetSuccess: 'Password reset',
    newPasswordRequired: 'Enter a new password',
    passwordMinLength: 'Password must be at least 8 characters',
    newPasswordPlaceholder: 'New password (at least 8 characters)',
    confirmPasswordRequired: 'Enter the new password again',
    passwordsMismatch: 'The new passwords do not match',
    confirmPasswordPlaceholder: 'Confirm new password',
    resendShortAfter: 'Resend in {seconds}s',
    previous: 'Previous',
    resetPassword: 'Reset password',
    resendCodeAfter: 'Resend code in {seconds}s',
    resendCode: 'Resend verification code',
    backToLogin: 'Back to sign in',
    countryCodeRequired: 'Select a country code',
    phoneRequired: 'Enter your phone number',
    phoneInvalid: 'Enter a valid phone number (digits only)',
    phonePlaceholder: 'Phone number',
    loading: 'Loading...',
    loadFailed: 'Could not load the content. Please try again later.',
    checkingLogin: 'Checking sign-in status...',
    initializingServices: 'Initializing services...',
    pleaseWait: 'Please wait. This may take a few seconds.',
    retryCount: 'Retry: {count}/{max}',
    initializationFailed: 'Service initialization failed, but the app will continue',
    passwordTooShort: 'Password must be at least 8 characters',
    passwordTooLong: 'Password cannot exceed 128 characters',
    passwordNeedsLowercase: 'Password must contain at least one lowercase letter',
    passwordNeedsUppercase: 'Password must contain at least one uppercase letter',
    passwordNeedsNumber: 'Password must contain at least one number',
    passwordNeedsSpecial: 'Password must contain at least one special character (such as !@#$%^&*)',
    otherDeviceLogin: 'Your account was signed in on another device',
    sessionExpired: 'Your session has expired. Please sign in again.',
    accountUnavailable: 'This account is unavailable. Contact an administrator.',
    authUnavailable: 'Authentication is temporarily unavailable. Please try again later.',
    loginAgain: 'Please sign in again',
    loginError: 'Could not sign in. Please try again later.',
    operationError: 'The operation failed. Please try again later.',
    networkError: 'Could not connect. Check your network settings and try again.',
    invalidCredentials: 'Incorrect account or password. Check them and try again.',
    serviceUnavailable: 'The service is temporarily unavailable. Please try again later.',
    serverError: 'Server error. Please try again later.',
    requestTimeout: 'The request timed out. Check your connection and try again.',
    invalidOrExpiredCode: 'The verification code is incorrect or expired. Request a new one.',
    emailCodeSent: 'If the account exists, a verification code has been sent to your email.',
    resetError: 'Could not reset the password. Please try again later.',
    passwordReset: 'Your password has been reset. Sign in with your new password.',
    badRequest: 'Invalid request. Check the information you entered.',
    authenticationFailed: 'Authentication failed. Please sign in again.',
    forbidden: 'You do not have permission to perform this action.',
    usernameExists: 'That username is already taken. Choose another one.',
    emailExists: 'That email is already registered. Use another email.',
    registrationError: 'Could not register. Please try again later.',
    safeLogout: 'You have signed out safely',
    profileLoadError: 'Could not load user information',
    profileUpdateError: 'Could not update your profile',
    updateError: 'Update failed',
    emailCodeToNewAddress: 'A verification code was sent to your new email address.',
    bindEmailError: 'Could not link the email address',
  },
  ru_RU: {
    language: 'Язык',
    languageAria: 'Выбрать язык интерфейса',
    usernameMethod: 'Имя пользователя',
    emailMethod: 'Эл. почта',
    adminMethod: 'Администратор',
    loginFailed: 'Не удалось войти',
    sendCodeFailed: 'Не удалось отправить код подтверждения',
    codeSent: 'Код подтверждения отправлен',
    usernameRequired: 'Введите имя пользователя',
    username: 'Имя пользователя',
    adminUsername: 'Имя администратора',
    emailRequired: 'Введите адрес эл. почты',
    emailInvalid: 'Введите действительный адрес эл. почты',
    registeredEmail: 'Эл. почта регистрации',
    passwordRequired: 'Введите пароль',
    password: 'Пароль',
    forgotPassword: 'Забыли пароль?',
    codeRequired: 'Введите код подтверждения',
    codeInvalid: 'Код подтверждения должен состоять из 6 цифр',
    codePlaceholder: '6-значный код',
    resendAfter: 'Повторная отправка через {seconds} с',
    sendCode: 'Отправить код',
    termsRequired: 'Прочитайте и примите Пользовательское соглашение и Политику конфиденциальности',
    termsPrefix: 'Я прочитал(-а) и принимаю',
    terms: 'Пользовательское соглашение',
    termsAnd: 'и',
    privacy: 'Политику конфиденциальности',
    loggingIn: 'Вход...',
    enterAdmin: 'Открыть панель администратора',
    login: 'Войти',
    desktopLogin: 'Войти на компьютере',
    mobileLogin: 'Войти на мобильном',
    noAccount: 'Нет учётной записи?',
    registerNow: 'Зарегистрироваться',
    registerSuccess: 'Регистрация завершена',
    registerSuccessDescription: 'Регистрация завершена! Добро пожаловать в Guohe WaveLink',
    registerFailed: 'Не удалось зарегистрироваться',
    usernameLength: 'Имя пользователя должно содержать 3–32 символа',
    usernamePattern: 'Допустимы буквы, цифры, подчёркивания и китайские иероглифы',
    passwordLength: 'Пароль должен содержать 8–128 символов',
    passwordHint: 'Пароль (от 8 символов, строчные и прописные буквы, цифра и спецсимвол)',
    passwordStrength: 'Надёжность пароля: {level}',
    strengthWeak: 'Низкая',
    strengthFair: 'Средняя',
    strengthGood: 'Хорошая',
    strengthStrong: 'Высокая',
    registering: 'Регистрация...',
    register: 'Зарегистрироваться',
    haveAccount: 'Уже есть учётная запись?',
    loginNow: 'Войти',
    recoverPassword: 'Восстановление пароля',
    recoverSubtitle: 'Сброс пароля с помощью кода из письма',
    verifyAccount: 'Проверка учётной записи',
    codeAndNewPassword: 'Код и новый пароль',
    complete: 'Готово',
    operationFailed: 'Операция не выполнена',
    resetSuccess: 'Пароль сброшен',
    newPasswordRequired: 'Введите новый пароль',
    passwordMinLength: 'Пароль должен содержать не менее 8 символов',
    newPasswordPlaceholder: 'Новый пароль (не менее 8 символов)',
    confirmPasswordRequired: 'Введите новый пароль ещё раз',
    passwordsMismatch: 'Новые пароли не совпадают',
    confirmPasswordPlaceholder: 'Подтвердите новый пароль',
    resendShortAfter: 'Повторить через {seconds} с',
    previous: 'Назад',
    resetPassword: 'Сбросить пароль',
    resendCodeAfter: 'Повторить отправку через {seconds} с',
    resendCode: 'Отправить код ещё раз',
    backToLogin: 'Вернуться ко входу',
    countryCodeRequired: 'Выберите код страны',
    phoneRequired: 'Введите номер телефона',
    phoneInvalid: 'Введите действительный номер телефона (только цифры)',
    phonePlaceholder: 'Номер телефона',
    loading: 'Загрузка...',
    loadFailed: 'Не удалось загрузить содержимое. Повторите попытку позже.',
    checkingLogin: 'Проверка статуса входа...',
    initializingServices: 'Инициализация служб...',
    pleaseWait: 'Подождите, это может занять несколько секунд.',
    retryCount: 'Попытка: {count}/{max}',
    initializationFailed: 'Не удалось инициализировать службы, но приложение продолжит работу',
    passwordTooShort: 'Пароль должен содержать не менее 8 символов',
    passwordTooLong: 'Пароль не может превышать 128 символов',
    passwordNeedsLowercase: 'Пароль должен содержать строчную букву',
    passwordNeedsUppercase: 'Пароль должен содержать прописную букву',
    passwordNeedsNumber: 'Пароль должен содержать цифру',
    passwordNeedsSpecial: 'Пароль должен содержать спецсимвол (например, !@#$%^&*)',
    otherDeviceLogin: 'В вашу учётную запись вошли на другом устройстве',
    sessionExpired: 'Срок сеанса истёк. Войдите снова.',
    accountUnavailable: 'Учётная запись недоступна. Обратитесь к администратору.',
    authUnavailable: 'Служба аутентификации временно недоступна. Повторите попытку позже.',
    loginAgain: 'Войдите снова',
    loginError: 'Не удалось войти. Повторите попытку позже.',
    operationError: 'Операция не выполнена. Повторите попытку позже.',
    networkError: 'Нет подключения. Проверьте настройки сети и повторите попытку.',
    invalidCredentials: 'Неверная учётная запись или пароль.',
    serviceUnavailable: 'Служба временно недоступна. Повторите попытку позже.',
    serverError: 'Ошибка сервера. Повторите попытку позже.',
    requestTimeout: 'Время ожидания истекло. Проверьте подключение и повторите попытку.',
    invalidOrExpiredCode: 'Код неверен или истёк. Запросите новый код.',
    emailCodeSent: 'Если учётная запись существует, код отправлен на вашу эл. почту.',
    resetError: 'Не удалось сбросить пароль. Повторите попытку позже.',
    passwordReset: 'Пароль сброшен. Войдите с новым паролем.',
    badRequest: 'Некорректный запрос. Проверьте введённые данные.',
    authenticationFailed: 'Ошибка аутентификации. Войдите снова.',
    forbidden: 'Недостаточно прав для выполнения операции.',
    usernameExists: 'Это имя пользователя уже занято. Выберите другое.',
    emailExists: 'Этот адрес уже зарегистрирован. Используйте другой.',
    registrationError: 'Не удалось зарегистрироваться. Повторите попытку позже.',
    safeLogout: 'Вы безопасно вышли из системы',
    profileLoadError: 'Не удалось загрузить данные пользователя',
    profileUpdateError: 'Не удалось обновить профиль',
    updateError: 'Не удалось обновить',
    emailCodeToNewAddress: 'Код отправлен на новый адрес эл. почты.',
    bindEmailError: 'Не удалось привязать адрес эл. почты',
  },
  es_ES: {
    language: 'Idioma',
    languageAria: 'Seleccionar idioma de la interfaz',
    usernameMethod: 'Usuario',
    emailMethod: 'Correo',
    adminMethod: 'Administrador',
    loginFailed: 'No se pudo iniciar sesión',
    sendCodeFailed: 'No se pudo enviar el código',
    codeSent: 'Código de verificación enviado',
    usernameRequired: 'Introduce tu nombre de usuario',
    username: 'Nombre de usuario',
    adminUsername: 'Usuario administrador',
    emailRequired: 'Introduce tu correo electrónico',
    emailInvalid: 'Introduce un correo electrónico válido',
    registeredEmail: 'Correo registrado',
    passwordRequired: 'Introduce tu contraseña',
    password: 'Contraseña',
    forgotPassword: '¿Has olvidado la contraseña?',
    codeRequired: 'Introduce el código de verificación',
    codeInvalid: 'El código debe tener 6 dígitos',
    codePlaceholder: 'Código de 6 dígitos',
    resendAfter: 'Reenviar en {seconds} s',
    sendCode: 'Enviar código',
    termsRequired: 'Lee y acepta los Términos de servicio y la Política de privacidad',
    termsPrefix: 'He leído y acepto los',
    terms: 'Términos de servicio',
    termsAnd: 'y la',
    privacy: 'Política de privacidad',
    loggingIn: 'Iniciando sesión...',
    enterAdmin: 'Abrir panel de administración',
    login: 'Iniciar sesión',
    desktopLogin: 'Acceso de escritorio',
    mobileLogin: 'Acceso móvil',
    noAccount: '¿No tienes una cuenta?',
    registerNow: 'Regístrate',
    registerSuccess: 'Registro completado',
    registerSuccessDescription: '¡Registro completado! Te damos la bienvenida a Guohe WaveLink',
    registerFailed: 'No se pudo completar el registro',
    usernameLength: 'El nombre de usuario debe tener entre 3 y 32 caracteres',
    usernamePattern: 'Solo se permiten letras, números, guiones bajos y caracteres chinos',
    passwordLength: 'La contraseña debe tener entre 8 y 128 caracteres',
    passwordHint: 'Contraseña (8+ caracteres, mayúsculas, minúsculas, un número y un símbolo)',
    passwordStrength: 'Seguridad de la contraseña: {level}',
    strengthWeak: 'Débil',
    strengthFair: 'Aceptable',
    strengthGood: 'Buena',
    strengthStrong: 'Fuerte',
    registering: 'Registrando...',
    register: 'Registrarse',
    haveAccount: '¿Ya tienes una cuenta?',
    loginNow: 'Inicia sesión',
    recoverPassword: 'Restablecer contraseña',
    recoverSubtitle: 'Restablece tu contraseña con un código enviado por correo',
    verifyAccount: 'Verificar cuenta',
    codeAndNewPassword: 'Código y nueva contraseña',
    complete: 'Listo',
    operationFailed: 'La operación ha fallado',
    resetSuccess: 'Contraseña restablecida',
    newPasswordRequired: 'Introduce una nueva contraseña',
    passwordMinLength: 'La contraseña debe tener al menos 8 caracteres',
    newPasswordPlaceholder: 'Nueva contraseña (mínimo 8 caracteres)',
    confirmPasswordRequired: 'Vuelve a introducir la nueva contraseña',
    passwordsMismatch: 'Las contraseñas nuevas no coinciden',
    confirmPasswordPlaceholder: 'Confirmar nueva contraseña',
    resendShortAfter: 'Reenviar en {seconds} s',
    previous: 'Anterior',
    resetPassword: 'Restablecer contraseña',
    resendCodeAfter: 'Reenviar código en {seconds} s',
    resendCode: 'Reenviar código',
    backToLogin: 'Volver al inicio de sesión',
    countryCodeRequired: 'Selecciona un código de país',
    phoneRequired: 'Introduce tu número de teléfono',
    phoneInvalid: 'Introduce un teléfono válido (solo dígitos)',
    phonePlaceholder: 'Número de teléfono',
    loading: 'Cargando...',
    loadFailed: 'No se pudo cargar el contenido. Inténtalo más tarde.',
    checkingLogin: 'Comprobando el inicio de sesión...',
    initializingServices: 'Inicializando servicios...',
    pleaseWait: 'Espera; puede tardar unos segundos.',
    retryCount: 'Reintento: {count}/{max}',
    initializationFailed: 'No se pudieron inicializar los servicios, pero la aplicación seguirá funcionando',
    passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
    passwordTooLong: 'La contraseña no puede superar los 128 caracteres',
    passwordNeedsLowercase: 'La contraseña debe incluir una letra minúscula',
    passwordNeedsUppercase: 'La contraseña debe incluir una letra mayúscula',
    passwordNeedsNumber: 'La contraseña debe incluir un número',
    passwordNeedsSpecial: 'La contraseña debe incluir un carácter especial (como !@#$%^&*)',
    otherDeviceLogin: 'Se ha iniciado sesión con tu cuenta en otro dispositivo',
    sessionExpired: 'La sesión ha caducado. Inicia sesión de nuevo.',
    accountUnavailable: 'La cuenta no está disponible. Contacta con un administrador.',
    authUnavailable: 'La autenticación no está disponible temporalmente. Inténtalo más tarde.',
    loginAgain: 'Inicia sesión de nuevo',
    loginError: 'No se pudo iniciar sesión. Inténtalo más tarde.',
    operationError: 'La operación ha fallado. Inténtalo más tarde.',
    networkError: 'No se pudo conectar. Comprueba la red e inténtalo de nuevo.',
    invalidCredentials: 'La cuenta o la contraseña son incorrectas.',
    serviceUnavailable: 'El servicio no está disponible temporalmente. Inténtalo más tarde.',
    serverError: 'Error del servidor. Inténtalo más tarde.',
    requestTimeout: 'La solicitud ha agotado el tiempo. Comprueba la conexión.',
    invalidOrExpiredCode: 'El código es incorrecto o ha caducado. Solicita uno nuevo.',
    emailCodeSent: 'Si la cuenta existe, se ha enviado un código a tu correo.',
    resetError: 'No se pudo restablecer la contraseña. Inténtalo más tarde.',
    passwordReset: 'Se ha restablecido la contraseña. Inicia sesión con la nueva.',
    badRequest: 'Solicitud no válida. Comprueba los datos introducidos.',
    authenticationFailed: 'La autenticación ha fallado. Inicia sesión de nuevo.',
    forbidden: 'No tienes permiso para realizar esta acción.',
    usernameExists: 'Ese nombre de usuario ya existe. Elige otro.',
    emailExists: 'Ese correo ya está registrado. Usa otro.',
    registrationError: 'No se pudo completar el registro. Inténtalo más tarde.',
    safeLogout: 'Has cerrado sesión de forma segura',
    profileLoadError: 'No se pudo cargar la información del usuario',
    profileUpdateError: 'No se pudo actualizar tu perfil',
    updateError: 'No se pudo actualizar',
    emailCodeToNewAddress: 'Se ha enviado un código a tu nueva dirección de correo.',
    bindEmailError: 'No se pudo vincular el correo',
  },
  fr_FR: {
    language: 'Langue',
    languageAria: "Sélectionner la langue de l'interface",
    usernameMethod: "Nom d'utilisateur",
    emailMethod: 'E-mail',
    adminMethod: 'Administrateur',
    loginFailed: 'Échec de la connexion',
    sendCodeFailed: "Impossible d'envoyer le code",
    codeSent: 'Code de vérification envoyé',
    usernameRequired: "Saisissez votre nom d'utilisateur",
    username: "Nom d'utilisateur",
    adminUsername: "Nom d'utilisateur administrateur",
    emailRequired: 'Saisissez votre adresse e-mail',
    emailInvalid: 'Saisissez une adresse e-mail valide',
    registeredEmail: "E-mail d'inscription",
    passwordRequired: 'Saisissez votre mot de passe',
    password: 'Mot de passe',
    forgotPassword: 'Mot de passe oublié ?',
    codeRequired: 'Saisissez le code de vérification',
    codeInvalid: 'Le code doit comporter 6 chiffres',
    codePlaceholder: 'Code à 6 chiffres',
    resendAfter: 'Renvoyer dans {seconds} s',
    sendCode: 'Envoyer le code',
    termsRequired: "Veuillez lire et accepter les Conditions d'utilisation et la Politique de confidentialité",
    termsPrefix: "J'ai lu et j'accepte les",
    terms: "Conditions d'utilisation",
    termsAnd: 'et la',
    privacy: 'Politique de confidentialité',
    loggingIn: 'Connexion...',
    enterAdmin: "Ouvrir l'espace administrateur",
    login: 'Se connecter',
    desktopLogin: 'Connexion ordinateur',
    mobileLogin: 'Connexion mobile',
    noAccount: 'Vous n’avez pas de compte ?',
    registerNow: "S'inscrire",
    registerSuccess: 'Inscription réussie',
    registerSuccessDescription: 'Inscription réussie ! Bienvenue sur Guohe WaveLink',
    registerFailed: "Échec de l'inscription",
    usernameLength: "Le nom d'utilisateur doit comporter entre 3 et 32 caractères",
    usernamePattern: 'Seuls les lettres, chiffres, traits de soulignement et caractères chinois sont autorisés',
    passwordLength: 'Le mot de passe doit comporter entre 8 et 128 caractères',
    passwordHint: 'Mot de passe (8+ caractères, majuscule, minuscule, chiffre et symbole)',
    passwordStrength: 'Robustesse du mot de passe : {level}',
    strengthWeak: 'Faible',
    strengthFair: 'Moyenne',
    strengthGood: 'Bonne',
    strengthStrong: 'Forte',
    registering: 'Inscription...',
    register: "S'inscrire",
    haveAccount: 'Vous avez déjà un compte ?',
    loginNow: 'Se connecter',
    recoverPassword: 'Réinitialiser le mot de passe',
    recoverSubtitle: 'Réinitialisez votre mot de passe avec un code reçu par e-mail',
    verifyAccount: 'Vérifier le compte',
    codeAndNewPassword: 'Code et nouveau mot de passe',
    complete: 'Terminé',
    operationFailed: "Échec de l'opération",
    resetSuccess: 'Mot de passe réinitialisé',
    newPasswordRequired: 'Saisissez un nouveau mot de passe',
    passwordMinLength: 'Le mot de passe doit comporter au moins 8 caractères',
    newPasswordPlaceholder: 'Nouveau mot de passe (8 caractères minimum)',
    confirmPasswordRequired: 'Saisissez à nouveau le nouveau mot de passe',
    passwordsMismatch: 'Les nouveaux mots de passe ne correspondent pas',
    confirmPasswordPlaceholder: 'Confirmer le nouveau mot de passe',
    resendShortAfter: 'Renvoyer dans {seconds} s',
    previous: 'Précédent',
    resetPassword: 'Réinitialiser le mot de passe',
    resendCodeAfter: 'Renvoyer le code dans {seconds} s',
    resendCode: 'Renvoyer le code',
    backToLogin: 'Retour à la connexion',
    countryCodeRequired: "Sélectionnez un indicatif de pays",
    phoneRequired: 'Saisissez votre numéro de téléphone',
    phoneInvalid: 'Saisissez un numéro valide (chiffres uniquement)',
    phonePlaceholder: 'Numéro de téléphone',
    loading: 'Chargement...',
    loadFailed: 'Impossible de charger le contenu. Réessayez plus tard.',
    checkingLogin: 'Vérification de la connexion...',
    initializingServices: 'Initialisation des services...',
    pleaseWait: 'Veuillez patienter, cela peut prendre quelques secondes.',
    retryCount: 'Nouvelle tentative : {count}/{max}',
    initializationFailed: "L'initialisation des services a échoué, mais l'application va continuer",
    passwordTooShort: 'Le mot de passe doit comporter au moins 8 caractères',
    passwordTooLong: 'Le mot de passe ne peut pas dépasser 128 caractères',
    passwordNeedsLowercase: 'Le mot de passe doit contenir une minuscule',
    passwordNeedsUppercase: 'Le mot de passe doit contenir une majuscule',
    passwordNeedsNumber: 'Le mot de passe doit contenir un chiffre',
    passwordNeedsSpecial: 'Le mot de passe doit contenir un caractère spécial (comme !@#$%^&*)',
    otherDeviceLogin: 'Votre compte a été connecté sur un autre appareil',
    sessionExpired: 'Votre session a expiré. Reconnectez-vous.',
    accountUnavailable: 'Ce compte est indisponible. Contactez un administrateur.',
    authUnavailable: "L'authentification est temporairement indisponible. Réessayez plus tard.",
    loginAgain: 'Veuillez vous reconnecter',
    loginError: 'Impossible de se connecter. Réessayez plus tard.',
    operationError: "L'opération a échoué. Réessayez plus tard.",
    networkError: 'Connexion impossible. Vérifiez votre réseau et réessayez.',
    invalidCredentials: 'Compte ou mot de passe incorrect.',
    serviceUnavailable: 'Le service est temporairement indisponible. Réessayez plus tard.',
    serverError: 'Erreur du serveur. Réessayez plus tard.',
    requestTimeout: "Le délai d'attente est dépassé. Vérifiez votre connexion.",
    invalidOrExpiredCode: 'Le code est incorrect ou expiré. Demandez-en un nouveau.',
    emailCodeSent: 'Si le compte existe, un code a été envoyé à votre adresse e-mail.',
    resetError: 'Impossible de réinitialiser le mot de passe. Réessayez plus tard.',
    passwordReset: 'Votre mot de passe a été réinitialisé. Connectez-vous avec le nouveau.',
    badRequest: 'Requête incorrecte. Vérifiez les informations saisies.',
    authenticationFailed: "Échec de l'authentification. Reconnectez-vous.",
    forbidden: "Vous n'avez pas l'autorisation d'effectuer cette action.",
    usernameExists: "Ce nom d'utilisateur est déjà pris. Choisissez-en un autre.",
    emailExists: 'Cette adresse e-mail est déjà enregistrée. Utilisez-en une autre.',
    registrationError: "Impossible de s'inscrire. Réessayez plus tard.",
    safeLogout: 'Vous êtes déconnecté en toute sécurité',
    profileLoadError: 'Impossible de charger les informations utilisateur',
    profileUpdateError: 'Impossible de mettre à jour votre profil',
    updateError: 'Échec de la mise à jour',
    emailCodeToNewAddress: 'Un code a été envoyé à votre nouvelle adresse e-mail.',
    bindEmailError: "Impossible d'associer l'adresse e-mail",
  },
  ja_JP: {
    language: '言語',
    languageAria: '表示言語を選択',
    usernameMethod: 'ユーザー名',
    emailMethod: 'メール',
    adminMethod: '管理者',
    loginFailed: 'ログインに失敗しました',
    sendCodeFailed: '認証コードを送信できませんでした',
    codeSent: '認証コードを送信しました',
    usernameRequired: 'ユーザー名を入力してください',
    username: 'ユーザー名',
    adminUsername: '管理者ユーザー名',
    emailRequired: 'メールアドレスを入力してください',
    emailInvalid: '有効なメールアドレスを入力してください',
    registeredEmail: '登録メールアドレス',
    passwordRequired: 'パスワードを入力してください',
    password: 'パスワード',
    forgotPassword: 'パスワードをお忘れですか？',
    codeRequired: '認証コードを入力してください',
    codeInvalid: '認証コードは 6 桁の数字です',
    codePlaceholder: '6 桁の認証コード',
    resendAfter: '{seconds} 秒後に再送信',
    sendCode: '認証コードを送信',
    termsRequired: '利用規約とプライバシーポリシーを読み、同意してください',
    termsPrefix: '次の内容を読み、同意します：',
    terms: '利用規約',
    termsAnd: 'および',
    privacy: 'プライバシーポリシー',
    loggingIn: 'ログイン中...',
    enterAdmin: '管理画面を開く',
    login: 'ログイン',
    desktopLogin: 'パソコンでログイン',
    mobileLogin: 'モバイルでログイン',
    noAccount: 'アカウントをお持ちでないですか？',
    registerNow: '今すぐ登録',
    registerSuccess: '登録が完了しました',
    registerSuccessDescription: '登録が完了しました！Guohe WaveLink へようこそ',
    registerFailed: '登録に失敗しました',
    usernameLength: 'ユーザー名は 3～32 文字で入力してください',
    usernamePattern: 'ユーザー名には英数字、アンダースコア、中国語文字のみ使用できます',
    passwordLength: 'パスワードは 8～128 文字で入力してください',
    passwordHint: 'パスワード（8 文字以上、大文字・小文字・数字・記号を含む）',
    passwordStrength: 'パスワード強度：{level}',
    strengthWeak: '弱い',
    strengthFair: '普通',
    strengthGood: '良い',
    strengthStrong: '強い',
    registering: '登録中...',
    register: '登録',
    haveAccount: 'すでにアカウントをお持ちですか？',
    loginNow: 'ログイン',
    recoverPassword: 'パスワードを再設定',
    recoverSubtitle: 'メール認証コードを使ってパスワードを再設定します',
    verifyAccount: 'アカウント確認',
    codeAndNewPassword: '認証コードと新しいパスワード',
    complete: '完了',
    operationFailed: '操作に失敗しました',
    resetSuccess: '再設定が完了しました',
    newPasswordRequired: '新しいパスワードを入力してください',
    passwordMinLength: 'パスワードは 8 文字以上必要です',
    newPasswordPlaceholder: '新しいパスワード（8 文字以上）',
    confirmPasswordRequired: '新しいパスワードをもう一度入力してください',
    passwordsMismatch: '新しいパスワードが一致しません',
    confirmPasswordPlaceholder: '新しいパスワードを確認',
    resendShortAfter: '{seconds} 秒後に再送信',
    previous: '戻る',
    resetPassword: 'パスワードを再設定',
    resendCodeAfter: '{seconds} 秒後に認証コードを再送信',
    resendCode: '認証コードを再送信',
    backToLogin: 'ログインに戻る',
    countryCodeRequired: '国番号を選択してください',
    phoneRequired: '電話番号を入力してください',
    phoneInvalid: '有効な電話番号を数字のみで入力してください',
    phonePlaceholder: '電話番号',
    loading: '読み込み中...',
    loadFailed: '読み込めませんでした。しばらくしてからもう一度お試しください。',
    checkingLogin: 'ログイン状態を確認中...',
    initializingServices: 'サービスを初期化中...',
    pleaseWait: '数秒かかる場合があります。そのままお待ちください。',
    retryCount: '再試行：{count}/{max}',
    initializationFailed: 'サービスの初期化に失敗しましたが、アプリは続行します',
    passwordTooShort: 'パスワードは 8 文字以上必要です',
    passwordTooLong: 'パスワードは 128 文字以内で入力してください',
    passwordNeedsLowercase: 'パスワードには小文字を 1 文字以上含めてください',
    passwordNeedsUppercase: 'パスワードには大文字を 1 文字以上含めてください',
    passwordNeedsNumber: 'パスワードには数字を 1 文字以上含めてください',
    passwordNeedsSpecial: 'パスワードには記号（!@#$%^&* など）を 1 文字以上含めてください',
    otherDeviceLogin: '別の端末でこのアカウントにログインしました',
    sessionExpired: 'セッションの有効期限が切れました。もう一度ログインしてください。',
    accountUnavailable: 'このアカウントは利用できません。管理者にお問い合わせください。',
    authUnavailable: '認証サービスは一時的に利用できません。しばらくしてからお試しください。',
    loginAgain: 'もう一度ログインしてください',
    loginError: 'ログインできませんでした。しばらくしてからお試しください。',
    operationError: '操作に失敗しました。しばらくしてからお試しください。',
    networkError: '接続できませんでした。ネットワーク設定を確認して再試行してください。',
    invalidCredentials: 'アカウントまたはパスワードが正しくありません。',
    serviceUnavailable: 'サービスは一時的に利用できません。しばらくしてからお試しください。',
    serverError: 'サーバーエラーが発生しました。しばらくしてからお試しください。',
    requestTimeout: 'リクエストがタイムアウトしました。接続を確認してください。',
    invalidOrExpiredCode: '認証コードが正しくないか、有効期限が切れています。再取得してください。',
    emailCodeSent: 'アカウントが存在する場合、認証コードをメールで送信しました。',
    resetError: 'パスワードを再設定できませんでした。しばらくしてからお試しください。',
    passwordReset: 'パスワードを再設定しました。新しいパスワードでログインしてください。',
    badRequest: 'リクエストが正しくありません。入力内容を確認してください。',
    authenticationFailed: '認証に失敗しました。もう一度ログインしてください。',
    forbidden: 'この操作を行う権限がありません。',
    usernameExists: 'このユーザー名はすでに使用されています。別の名前を選んでください。',
    emailExists: 'このメールアドレスはすでに登録されています。別のアドレスを使用してください。',
    registrationError: '登録できませんでした。しばらくしてからお試しください。',
    safeLogout: '安全にログアウトしました',
    profileLoadError: 'ユーザー情報を取得できませんでした',
    profileUpdateError: 'プロフィールを更新できませんでした',
    updateError: '更新に失敗しました',
    emailCodeToNewAddress: '新しいメールアドレスに認証コードを送信しました。',
    bindEmailError: 'メールアドレスを連携できませんでした',
  },
};

export type AuthLocaleKey = keyof AuthMessages;
